/**
 * Entrar con USUARIO a través de la edge `login-with-username` (M32 / T10-03).
 *
 * Antes el navegador pedía sin sesión el correo de acceso de ese usuario
 * (`resolve_login_email_by_username`) y entraba con él: cualquiera sacaba el
 * correo de cualquiera. Ahora usuario y contraseña van a la edge, que devuelve
 * solo la sesión.
 *
 * TRANSICIÓN: si la edge todavía NO está desplegada (404) o no responde, se entra
 * como antes (función de la base). Así el orden de despliegue no deja a nadie sin
 * entrar. Cuando se aplique la migración 20260926140300 (que le quita esa función
 * al navegador), el camino viejo simplemente falla y solo queda la edge — y este
 * `fallback` se puede borrar.
 */
import { supabase } from "@/integrations/supabase/client";

export type UsernameLoginOutcome =
  | { kind: "session"; session: { access_token: string; refresh_token: string } }
  | { kind: "fallback" }
  | { kind: "error"; code: "invalid_credentials" | "ambiguous" | "rate_limited" };

/** Pura: estado HTTP (null = no llegó) + cuerpo → qué hacer. */
export function interpretUsernameLogin(status: number | null, body: unknown): UsernameLoginOutcome {
  const b = (body ?? {}) as { session?: { access_token?: string; refresh_token?: string }; error?: string };
  if (status !== null && status >= 200 && status < 300) {
    if (b.session?.access_token && b.session?.refresh_token) {
      return { kind: "session", session: { access_token: b.session.access_token, refresh_token: b.session.refresh_token } };
    }
    return { kind: "fallback" };
  }
  if (status === 401) return { kind: "error", code: "invalid_credentials" };
  if (status === 409) return { kind: "error", code: "ambiguous" };
  if (status === 429) return { kind: "error", code: "rate_limited" };
  // 404 (edge sin desplegar), 400 raro, 5xx o sin red: el camino de antes.
  return { kind: "fallback" };
}

export async function loginWithUsername(username: string, password: string): Promise<UsernameLoginOutcome> {
  try {
    const { data, error } = await supabase.functions.invoke("login-with-username", {
      body: { username, password },
    });
    if (!error) return interpretUsernameLogin(200, data);
    const ctx = (error as { context?: Response }).context;
    if (ctx && typeof ctx.status === "number") {
      let body: unknown = null;
      try {
        body = await ctx.json();
      } catch {
        body = null;
      }
      return interpretUsernameLogin(ctx.status, body);
    }
    return interpretUsernameLogin(null, null);
  } catch {
    return interpretUsernameLogin(null, null);
  }
}
